import { OverviewView, OverviewViewProps } from './Overview.view';
import React, { useContext, useEffect } from 'react';
import {
  GetAnalyticsOverviewDataLazyQueryHookResult,
  PostStatsResult,
  ProfileFollowersStats
} from '../../../../models/graphql';
import { AnalyticsContext } from '../../AnalyticsProvider';
import { format } from 'date-fns';
import { DateStringFormat } from 'constants/time-formats';

export type OverviewControllerProps = {
  overviewData: GetAnalyticsOverviewDataLazyQueryHookResult[1]['data'];
  loading: boolean;
  getAnalyticsDataQuery: GetAnalyticsOverviewDataLazyQueryHookResult[0];
};

const getTotalFollowers = (followersByProfile: ProfileFollowersStats[] | undefined, hasProfilesSelected: boolean) => {
  if (!followersByProfile || !hasProfilesSelected) {
    return null;
  }
  const isEveryElementNull = followersByProfile.every(
    profileFollowers => !profileFollowers.followersStats.some(followerStat => followerStat.followers !== null)
  );

  if (isEveryElementNull) {
    return null;
  }

  return followersByProfile.reduce((acc, currentValue) => {
    const copyFollowers = [...currentValue.followersStats];
    const revertedCopy = copyFollowers.reverse();
    const firstThatIsNumber = revertedCopy.find(
      followerCount => typeof followerCount.followers === 'number'
    )?.followers;
    const totalProfileFollowers = firstThatIsNumber ?? 0;
    return acc + totalProfileFollowers;
  }, 0);
};

type MetricsOverview = {
  engagement: number | null | undefined;
  impressions: number | null | undefined;
  totalPosts: number | null | undefined;
};
const getTotalMetrics = (
  postStatsByProfile: PostStatsResult | undefined,
  hasProfilesSelected: boolean
): MetricsOverview => {
  if (!postStatsByProfile || !postStatsByProfile.postStatsByProfile || !hasProfilesSelected) {
    return { engagement: null, totalPosts: null, impressions: null };
  }
  return postStatsByProfile.postStatsByProfile.reduce<MetricsOverview>(
    (currentEngagement, currentValue) => {
      const likes = currentValue.postStats[0].likes;
      const comments = currentValue.postStats[0].comments;
      const shares = currentValue.postStats[0].shares;
      const impressions = currentValue.postStats[0].impressions;
      const postCount = currentValue.postStats[0].postCount;
      const engagement =
        likes === null && shares === null && comments === null ? null : (likes ?? 0) + (shares ?? 0) + (comments ?? 0);
      return {
        engagement:
          typeof currentEngagement.engagement === 'number'
            ? currentEngagement.engagement + (engagement ?? 0)
            : engagement,
        impressions:
          typeof currentEngagement.impressions === 'number'
            ? currentEngagement.impressions + (impressions ?? 0)
            : impressions,
        totalPosts:
          typeof currentEngagement.totalPosts === 'number' ? currentEngagement.totalPosts + postCount : postCount
      };
    },
    { engagement: null, impressions: null, totalPosts: null }
  );
};

export const OverviewController = ({ overviewData, loading, getAnalyticsDataQuery }: OverviewControllerProps) => {
  const { startDate, endDate, selectedProfilesIds } = useContext(AnalyticsContext);
  const formattedStartDate = format(startDate, DateStringFormat);
  const formattedEndDate = format(endDate, DateStringFormat);

  useEffect(() => {
    const executeQuery = async () => {
      await getAnalyticsDataQuery({
        variables: { startDate: formattedStartDate, endDate: formattedEndDate, selectedProfilesIds },
        fetchPolicy: 'no-cache'
      });
    };
    if (selectedProfilesIds && selectedProfilesIds.length > 0) {
      executeQuery();
    }
  }, [formattedStartDate, formattedEndDate, selectedProfilesIds, getAnalyticsDataQuery]);
  const followersProfile = overviewData?.analyticsFollowers;
  const postStatsByProfile = overviewData?.analyticsPostStats;
  const totalFollowers = getTotalFollowers(followersProfile, selectedProfilesIds.length >= 1);
  const { engagement, totalPosts, impressions } = getTotalMetrics(postStatsByProfile, selectedProfilesIds.length >= 1);
  const totalEngagement = engagement;
  const props: OverviewViewProps = {
    totalFollowers,
    totalEngagement,
    totalPosts,
    impressions,
    followersByProfile: followersProfile ?? [],
    loading
  };
  return <OverviewView {...props}></OverviewView>;
};
